import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardMedia,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import {
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx,
  dialogFormActionsSx,
  uploadDropzoneSx,
  uploadDropzoneCloudIconSx
} from '../utils/dialogFormLayout';

const ItemForm = ({ open, onClose, onSubmit, item = null, isEdit = false }) => {
  const [name, setName] = useState('');
  const [cost, setCost] = useState('');
  const [type, setType] = useState('Gift');
  const [description, setDescription] = useState('');
  const [instruction, setInstruction] = useState('');
  const [icon, setIcon] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const fileInputRef = useRef();

  // Update form fields when item prop changes
  useEffect(() => {
    if (item) {
      setName(item.name || '');
      setCost(item.cost || '');
      setType(item.type || 'Gift');
      setDescription(item.description || '');
      setInstruction(item.instruction || '');
      setIcon(item.icon || '');
      setIconFile(null);
    } else {
      setName('');
      setCost('');
      setType('Gift');
      setDescription('');
      setInstruction('');
      setIcon('');
      setIconFile(null);
    }
  }, [item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() && cost !== '') {
      onSubmit({ 
        name: name.trim(),
        cost: Number(cost),
        type: type,
        description: description.trim(),
        instruction: instruction.trim(),
        iconFile,
        existingIcon: icon && !iconFile ? icon : null
      });
      setName('');
      setCost('');
      setType('Gift');
      setDescription('');
      setInstruction('');
      setIcon('');
      setIconFile(null);
      onClose();
    }
  };

  const handleClose = () => {
    setName('');
    setCost('');
    setType('Gift');
    setDescription('');
    setInstruction('');
    setIcon('');
    setIconFile(null);
    onClose();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIconFile(file);
      setIcon(URL.createObjectURL(file));
    }
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setIcon('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: dialogFormPaperSx }}
    >
      <DialogTitle sx={dialogFormTitleSx}>
        {isEdit ? 'Edit Item' : 'Create New Item'}
      </DialogTitle>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
          <TextField
            autoFocus
            margin="dense"
            label="Item Name"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Cost"
            type="number"
            fullWidth
            variant="outlined"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            required
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value)}
            >
              <MenuItem value="Gift">Gift</MenuItem>
              <MenuItem value="AnalPlug">AnalPlug</MenuItem>
              <MenuItem value="Stack">Stack</MenuItem>
              <MenuItem value="Dildo">Dildo</MenuItem>
              <MenuItem value="MistressDildo">MistressDildo</MenuItem>
            </Select>
          </FormControl>

          {/* Icon Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Item Icon
            </Typography>
            {icon ? (
              <Card sx={{ mb: 2, maxWidth: 200 }}>
                <CardMedia
                  component="img"
                  height="150"
                  image={icon}
                  alt="Icon"
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {iconFile ? iconFile.name : 'Item icon'}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveIcon} color="error">
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            ) : (
              <Box sx={(theme) => uploadDropzoneSx(theme)} onClick={openFileDialog}>
                <CloudUpload sx={uploadDropzoneCloudIconSx} />
                <Typography variant="body1" color="text.secondary">
                  Click to upload item icon
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports: JPG, PNG, GIF
                </Typography>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>
          
          <TextField
            margin="dense"
            label="Description"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter item description..."
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Instruction"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Enter instruction text for this item..."
          />
        </DialogContent>
        <DialogActions sx={dialogFormActionsSx}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ItemForm; 